import { atom } from 'jotai';
import { unwrap } from 'jotai/utils';
import { teamCompetitionOptionsAtom, teamGamesAtom, teamSeasonOptionsAtom } from '@/atoms/queries/teams';
import { FormattedGame, FormattedGameWithTeam } from '@/types/game';
import { getFilteredGames, getGamesBySeason, getSelectedGames } from '@/utils/atoms/game';
import {
  filterGamesByCompetition,
  filterGamesByGameType,
  filterGamesBySeason,
  pipeFilterFunctions,
} from '@/utils/filters';
import { loadable } from '@/utils/jotai';
import { convertToDropdownOption } from '@/utils/object';
import { filtersAtom } from '../filter';
import { competitionIdsToFilterByAtom } from '../highLevel/competitions';
import { seasonIdsToFilterByAtom } from '../highLevel/seasons';

const teamGameFilterFunctionsAtom = atom(get => {
  const selectedGameType = get(filtersAtom).gameType.value;
  const selectedCompetitionIds = get(competitionIdsToFilterByAtom);
  const selectedSeasonIds = get(seasonIdsToFilterByAtom);

  return pipeFilterFunctions<FormattedGame>(
    (entityGame: FormattedGameWithTeam[]) => filterGamesByCompetition(entityGame, selectedCompetitionIds),
    (entityGame: FormattedGameWithTeam[]) => filterGamesBySeason(entityGame, selectedSeasonIds),
    (entityGame: FormattedGameWithTeam[]) => filterGamesByGameType(entityGame, selectedGameType),
  );
});

export const filteredTeamGamesAtom = atom(async get => {
  const { startDate, endDate, gameRange, periodRange } = get(filtersAtom);
  return getFilteredGames(get, teamGamesAtom, teamGameFilterFunctionsAtom, {
    startDate,
    endDate,
    gameRange,
    periodRange,
  });
});

const teamGamesOptionsAtom = atom(async get => {
  const teamGames = await get(filteredTeamGamesAtom);
  return teamGames.map(({ game: { gameId, name } }) => convertToDropdownOption(name, gameId));
});

export const teamSelectedGamesAtom = atom(async get =>
  getSelectedGames(get, teamGamesOptionsAtom, get(filtersAtom).deselectedGameIds),
);

const teamSelectedGamesForQueryAtom = atom(async get => {
  const userTeamSelectedGames = await get(teamSelectedGamesAtom);
  return userTeamSelectedGames.length > 0 ? userTeamSelectedGames : get(teamGamesOptionsAtom);
});

export const unwrappedTeamSelectedGamesForQueryAtom = unwrap(teamSelectedGamesForQueryAtom, prev => prev || []);

export const loadableTeamSelectedGamesForQueryAtom = loadable(teamSelectedGamesForQueryAtom);

export const teamGamesBySeasonAtom = atom(get =>
  getGamesBySeason(get, teamSeasonOptionsAtom, teamCompetitionOptionsAtom, filteredTeamGamesAtom),
);
